import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Print as PrintIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api'; // This should work now

const steps = ['Customer & Device', 'Diagnosis', 'Quotation', 'Parts', 'Completion'];

const RepairForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    customer_id: '',
    device_type: '',
    device_model: '',
    device_serial: '',
    device_issue: '',
    priority: 'medium',
    estimated_completion: '',
    technician_id: '',
    technician_name: '',
    notes: ''
  });
  
  const [diagnosis, setDiagnosis] = useState({
    diagnosis: '',
    estimated_cost: 0
  });
  
  const [quotationItems, setQuotationItems] = useState([]);
  const [repairParts, setRepairParts] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  
  const [newQuotationItem, setNewQuotationItem] = useState({
    item_type: 'part',
    description: '',
    quantity: 1,
    unit_price: 0,
    discount_percentage: 0,
    tax_rate: 0,
    notes: ''
  });

  useEffect(() => {
    fetchCustomers();
    fetchProducts();
    fetchDeviceTypes();
    fetchTechnicians();
    if (id) {
      fetchRepair();
    }
  }, [id]);

  const fetchRepair = async () => {
    try {
      const response = await api.getRepairById(id);
      if (response.success) {
        setFormData(response.data);
      }
    } catch (error) {
      console.error('Error fetching repair:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.getCustomers();
      if (response.success) {
        setCustomers(response.data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.getProducts();
      if (response.success) {
        setProducts(response.data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchDeviceTypes = async () => {
    try {
      const response = await api.getDeviceTypes();
      if (response.success) {
        setDeviceTypes(response.data);
      }
    } catch (error) {
      console.error('Error fetching device types:', error);
      // Default device types if API fails
      setDeviceTypes([
        'Laptop', 'Desktop', 'Mobile Phone', 'Tablet', 'Printer', 
        'Monitor', 'TV', 'Camera', 'Other'
      ]);
    }
  };

  const fetchTechnicians = async () => {
    try {
      const response = await api.getTechnicians();
      if (response.success) {
        setTechnicians(response.data);
      }
    } catch (error) {
      console.error('Error fetching technicians:', error);
      // Default technicians if API fails
      setTechnicians([
        { id: 1, name: 'Admin User', role: 'admin' },
        { id: 2, name: 'Technician 1', role: 'technician' },
        { id: 3, name: 'Technician 2', role: 'technician' }
      ]);
    }
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSaveRepair = async () => {
    try {
      setLoading(true);
      let response;
      
      if (id) {
        // Update existing repair
        response = await api.updateRepair(id, formData);
      } else {
        // Create new repair
        response = await api.createRepair(formData);
      }
      
      if (response.success) {
        if (!id) {
          // If creating new repair, navigate to edit page with the new ID
          navigate(`/repairs/edit/${response.data.id}`);
        } else {
          alert('Repair updated successfully!');
        }
      }
    } catch (error) {
      console.error('Error saving repair:', error);
      alert(`Error saving repair: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Customer & Device Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Customer</InputLabel>
                <Select
                  name="customer_id"
                  value={formData.customer_id}
                  onChange={handleFormChange}
                  label="Customer"
                >
                  <MenuItem value="">Select Customer</MenuItem>
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.name} - {customer.customer_code || customer.email || customer.phone}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Device Type</InputLabel>
                <Select
                  name="device_type"
                  value={formData.device_type}
                  onChange={handleFormChange}
                  label="Device Type"
                >
                  <MenuItem value="">Select Device Type</MenuItem>
                  {deviceTypes.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Device Model"
                name="device_model"
                value={formData.device_model}
                onChange={handleFormChange}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Serial Number"
                name="device_serial"
                value={formData.device_serial}
                onChange={handleFormChange}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Device Issue"
                name="device_issue"
                value={formData.device_issue}
                onChange={handleFormChange}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleFormChange}
                  label="Priority"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Estimated Completion"
                name="estimated_completion"
                type="date"
                value={formData.estimated_completion}
                onChange={handleFormChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Technician</InputLabel>
                <Select
                  name="technician_id"
                  value={formData.technician_id}
                  onChange={handleFormChange}
                  label="Technician"
                >
                  <MenuItem value="">Select Technician</MenuItem>
                  {technicians.map((tech) => (
                    <MenuItem key={tech.id} value={tech.id}>
                      {tech.name} ({tech.role})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Additional Notes"
                name="notes"
                value={formData.notes}
                onChange={handleFormChange}
              />
            </Grid>
          </Grid>
        );
        
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Diagnosis & Assessment
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Diagnosis Details"
                value={diagnosis.diagnosis}
                onChange={(e) => setDiagnosis({...diagnosis, diagnosis: e.target.value})}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Estimated Cost"
                type="number"
                value={diagnosis.estimated_cost}
                onChange={(e) => setDiagnosis({...diagnosis, estimated_cost: parseFloat(e.target.value) || 0})}
                InputProps={{
                  startAdornment: <MoneyIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  try {
                    const response = await api.updateDiagnosis(id, diagnosis);
                    if (response.success) {
                      alert('Diagnosis saved successfully!');
                      handleNext();
                    }
                  } catch (error) {
                    console.error('Error saving diagnosis:', error);
                    alert(`Error saving diagnosis: ${error.message}`);
                  }
                }}
                disabled={!id || !diagnosis.diagnosis}
              >
                Save Diagnosis & Continue
              </Button>
            </Grid>
          </Grid>
        );
        
      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Create Quotation
              </Typography>
            </Grid>
            
            {/* Add quotation item form */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Add Quotation Item
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      select
                      label="Item Type"
                      value={newQuotationItem.item_type}
                      onChange={(e) => setNewQuotationItem({...newQuotationItem, item_type: e.target.value})}
                    >
                      <MenuItem value="part">Part</MenuItem>
                      <MenuItem value="labor">Labor</MenuItem>
                      <MenuItem value="service">Service</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </TextField>
                  </Grid>
                  
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={newQuotationItem.description}
                      onChange={(e) => setNewQuotationItem({...newQuotationItem, description: e.target.value})}
                    />
                  </Grid>
                  
                  <Grid item xs={6} md={1}>
                    <TextField
                      fullWidth
                      label="Qty"
                      type="number"
                      value={newQuotationItem.quantity}
                      onChange={(e) => setNewQuotationItem({...newQuotationItem, quantity: parseFloat(e.target.value) || 1})}
                    />
                  </Grid>
                  
                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      label="Unit Price"
                      type="number"
                      value={newQuotationItem.unit_price}
                      onChange={(e) => setNewQuotationItem({...newQuotationItem, unit_price: parseFloat(e.target.value) || 0})}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={1}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        if (newQuotationItem.description && newQuotationItem.unit_price > 0) {
                          setQuotationItems([...quotationItems, { 
                            ...newQuotationItem, 
                            id: Date.now(),
                            line_total: newQuotationItem.quantity * newQuotationItem.unit_price
                          }]);
                          setNewQuotationItem({
                            item_type: 'part',
                            description: '',
                            quantity: 1,
                            unit_price: 0,
                            discount_percentage: 0,
                            tax_rate: 0,
                            notes: ''
                          });
                        }
                      }}
                    >
                      Add
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            {/* Quotation items table */}
            <Grid item xs={12}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Qty</TableCell>
                      <TableCell>Unit Price</TableCell>
                      <TableCell>Total</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {quotationItems.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.item_type}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>LKR {item.unit_price.toFixed(2)}</TableCell>
                        <TableCell>LKR {(item.quantity * item.unit_price).toFixed(2)}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const newItems = [...quotationItems];
                              newItems.splice(index, 1);
                              setQuotationItems(newItems);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            
            {/* Quotation summary */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Quotation Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography>Subtotal:</Typography>
                    </Grid>
                    <Grid item xs={6} textAlign="right">
                      <Typography fontWeight="bold">
                        LKR {quotationItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0).toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  <Box mt={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={async () => {
                        try {
                          const quotationData = {
                            items: quotationItems,
                            valid_until: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                            notes: "Repair quotation"
                          };
                          
                          const response = await api.createRepairQuotation(id, quotationData);
                          if (response.success) {
                            alert('Quotation created successfully!');
                            handleNext();
                          }
                        } catch (error) {
                          console.error('Error creating quotation:', error);
                          alert(`Error creating quotation: ${error.message}`);
                        }
                      }}
                      disabled={!id || quotationItems.length === 0}
                    >
                      Create Quotation
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );
        
      default:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Complete Repair Process
              </Typography>
              <Typography>
                Please use the individual action buttons in the repair view to:
              </Typography>
              <ul>
                <li>Accept quotation and approve repair</li>
                <li>Add parts to the repair</li>
                <li>Mark repair as complete</li>
                <li>Record payments</li>
                <li>Mark as delivered</li>
              </ul>
              <Button
                variant="contained"
                onClick={() => navigate(`/repairs/${id}`)}
              >
                Go to Repair Details
              </Button>
            </Grid>
          </Grid>
        );
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {id ? 'Edit Repair' : 'New Repair'}
        </Typography>
        <Box>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/repairs')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          {activeStep === 0 && (
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveRepair}
              disabled={loading || !formData.customer_id || !formData.device_type || !formData.device_model || !formData.device_issue}
            >
              {id ? 'Update' : 'Create'} Repair
            </Button>
          )}
        </Box>
      </Box>

      {/* Stepper - Only show if we have an ID (existing repair) */}
      {id && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>
      )}

      {/* Step Content */}
      <Paper sx={{ p: 3 }}>
        {renderStepContent(activeStep)}
        
        {/* Navigation Buttons - Only show if we have an ID */}
        {id && activeStep > 0 && activeStep < steps.length - 1 && (
          <Box display="flex" justifyContent="space-between" mt={4}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={activeStep === steps.length - 1}
            >
              Next
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default RepairForm;